import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import apiClient from '../api/client';
import { PROMO_30_KITCHEN } from '../lib/promoCopy';
import { toast } from "sonner";
import { Loader2, ArrowLeft, Printer, Mail, Phone, MapPin, Check, X, ChevronLeft, ChevronRight, ExternalLink } from "lucide-react";

const CASE_STATUSES = ['New', 'Under review', 'Need video', 'Quoted', 'Booked', 'Completed'];

/** Absolut URL för foto (Cloudinary/https eller relativ mot backend). */
function resolvePhotoUrl(url) {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  return url.startsWith("/") ? `${base}${url}` : `${base}/${url}`;
}

export default function AdminCaseDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  /** Index i photos-arrayen när lightbox är öppen, annars null */
  const [lightboxIndex, setLightboxIndex] = useState(null);

  useEffect(() => {
    const fetchCase = async () => {
      try {
        const res = await apiClient.get(`/cases/${id}`, { headers: { Authorization: `Bearer ${localStorage.getItem('admin_token') || ''}` } });
        if (typeof res.data === "string" && /<!doctype html|<html/i.test(res.data)) {
          throw new Error(
            "API-svaret var HTML (index.html) istället för JSON. I Vercel: sätt Root Directory till mappen frontend, eller låt Root Directory vara repots och använd vercel.json i roten (symlink api skapas vid install). Alternativt sätt REACT_APP_API_BASE till full API-rot (…/api) eller REACT_APP_BACKEND_URL till backend-host."
          );
        }
        // Some deployments wrap payloads (e.g. { data: case }, { case: case }).
        const raw = res.data;
        const normalized =
          raw?.case ??
          raw?.caseData ??
          raw?.data ??
          raw;
        setCaseData(normalized);
      } catch (e) {
        const msg = e?.message || "Kunde inte hämta ärendet.";
        toast.error(msg);
      } finally {
        setLoading(false);
      }
    };
    fetchCase();
  }, [id]);

  useEffect(() => {
    if (lightboxIndex === null) return;
    const count = caseData?.photos?.length ?? 0;
    const onKey = (e) => {
      if (e.key === "ArrowLeft") {
        setLightboxIndex((i) => {
          if (i === null || !count) return i;
          return i === 0 ? count - 1 : i - 1;
        });
      }
      if (e.key === "ArrowRight") {
        setLightboxIndex((i) => {
          if (i === null || !count) return i;
          return i === count - 1 ? 0 : i + 1;
        });
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightboxIndex, caseData?.photos?.length]);

  const photoCount = caseData?.photos?.length ?? 0;

  const updateStatus = async (newStatus) => {
    try {
        const res = await apiClient.patch(`/cases/${id}`, { status: newStatus }, { headers: { Authorization: `Bearer ${localStorage.getItem('admin_token') || ''}` } });
        setCaseData(res.data);
        toast.success("Status uppdaterad");
    } catch (e) {
        toast.error("Kunde inte uppdatera status");
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) return <div className="flex h-screen items-center justify-center"><Loader2 className="animate-spin h-8 w-8" /></div>;
  if (!caseData) return <div className="p-8">Ärendet hittades inte.</div>;

  // Be tolerant to legacy/partial documents. Render what we have.
  const config = caseData.config ?? {};
  const contact = caseData.contact ?? {};
  const result = caseData.result ?? {};
  const photos = caseData.photos ?? [];
  const dimensions = caseData.dimensions ?? {};
  const isComplete = Boolean(caseData.config && caseData.contact && caseData.result);

  const statusForSelect = CASE_STATUSES.includes(caseData.status) ? caseData.status : 'New';
  const isPrivat = config?.customer_type === 'privat';
  const photoList = Array.isArray(photos) ? photos : [];

  const NOT_FILLED = "Ej ifyllt";
  const n = (v) => (v == null || v === "" ? null : Number(v));
  const fmtKr = (v) => (v != null && Number.isFinite(n(v)) ? `${n(v).toLocaleString('sv-SE')} kr` : NOT_FILLED);
  const fmtH = (v) => (v != null && Number.isFinite(n(v)) ? `${n(v)} h` : NOT_FILLED);

  const fullAddress = (() => {
    const street = [contact.address_street, contact.address_number].filter(Boolean).join(" ").trim();
    const zipCity = [contact.address_zip, contact.address_city].filter(Boolean).join(" ").trim();
    if (!street && !zipCity) return contact.address || NOT_FILLED;
    if (!street) return zipCity;
    if (!zipCity) return street;
    return `${street}, ${zipCity}`;
  })();

  return (
    <div className="min-h-screen bg-slate-50 p-6 md:p-10 print:p-0 print:bg-white">
      {/* Print Styles */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .print-break-inside { break-inside: avoid; }
          body { background: white; }
          .print-full-width { width: 100% !important; max-width: none !important; }
        }
      `}</style>

      <div className="max-w-5xl mx-auto space-y-6 print-full-width">
        {/* Navigation - Hidden on Print */}
        <div className="flex items-center justify-between no-print">
             <Button variant="outline" onClick={() => navigate('/admin/dashboard')}><ArrowLeft className="mr-2 h-4 w-4" /> Tillbaka</Button>
             <div className="flex gap-2">
                <Button variant="outline" onClick={handlePrint}><Printer className="mr-2 h-4 w-4" /> Skriv ut / PDF</Button>
             </div>
        </div>
        
        {!isComplete && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950 no-print">
            <p className="font-semibold">Ofullständigt ärende</p>
            <p className="mt-1 text-amber-900/90">
              Datat saknar delar (konfiguration, kunduppgifter eller kalkylresultat). Vyn försöker ändå visa det som finns.
            </p>
            <details className="mt-2">
              <summary className="cursor-pointer text-xs font-semibold text-amber-900/90">
                Visa rådata (för felsökning)
              </summary>
              <pre className="mt-2 max-h-64 overflow-auto rounded-lg border border-amber-200 bg-white/60 p-3 text-[11px] leading-snug text-amber-950">
                {JSON.stringify(caseData, null, 2)}
              </pre>
            </details>
          </div>
        )}

        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 pb-4 border-b">
            <div>
                <h1 className="text-3xl font-bold text-primary">Offertunderlag #{caseData.case_number ?? NOT_FILLED}</h1>
                <div className="text-muted-foreground">
                  Datum: {caseData.created_at ? new Date(caseData.created_at).toLocaleDateString('sv-SE') : NOT_FILLED}
                </div>
            </div>
            <div className="flex flex-col items-end gap-2 no-print">
                {caseData.status && !CASE_STATUSES.includes(caseData.status) && (
                    <span className="text-xs text-amber-700 font-medium max-w-[180px] text-right">
                      Oväntad status i DB: &quot;{caseData.status}&quot;. Välj ny status nedan.
                    </span>
                )}
                <Select value={statusForSelect} onValueChange={updateStatus}>
                    <SelectTrigger className="w-[180px]">
                        <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="New">Ny</SelectItem>
                        <SelectItem value="Under review">Under granskning</SelectItem>
                        <SelectItem value="Need video">Behöver video</SelectItem>
                        <SelectItem value="Quoted">Offererad</SelectItem>
                        <SelectItem value="Booked">Bokad</SelectItem>
                        <SelectItem value="Completed">Avslutad</SelectItem>
                    </SelectContent>
                </Select>
            </div>
        </div>

        <div className="rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-950 no-print print:hidden">
          <p className="font-semibold">Kundresa: pris efter kontakt</p>
          <p className="mt-1 text-green-900/90">
            Kalkylen visade ett <strong>suddigt pris</strong> tills kunden skickade in kontaktuppgifterna; denna vy visar samma siffror som kunden sedan fick se.
            {isPrivat && (
              <>
                {' '}
                <strong>{PROMO_30_KITCHEN}</strong> (ROT enligt Skatteverkets regler) ingår i bedömningen för privatperson.
              </>
            )}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Left Column: Contact & Price */}
            <div className="space-y-6 print-break-inside">
                <Card className="print:shadow-none print:border">
                    <CardHeader>
                        <CardTitle>Kund</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                        <div className="font-bold text-lg">{contact.name ?? NOT_FILLED}</div>
                        <div className="flex items-center text-sm"><Mail className="mr-2 h-4 w-4 text-muted-foreground" /> {contact.email ?? NOT_FILLED}</div>
                        <div className="flex items-center text-sm"><Phone className="mr-2 h-4 w-4 text-muted-foreground" /> {contact.phone ?? NOT_FILLED}</div>
                        <div className="flex items-center text-sm"><MapPin className="mr-2 h-4 w-4 text-muted-foreground shrink-0" /> <span>{fullAddress}</span></div>
                        <Badge variant="secondary" className="mt-2">{isPrivat ? 'Privatperson' : 'Företag'}</Badge>
                    </CardContent>
                </Card>

                <Card className="bg-slate-900 text-white border-slate-800 print:bg-white print:text-black print:border-black">
                    <CardHeader>
                        <CardTitle className="text-white print:text-black">Kalkylresultat</CardTitle>
                        {isPrivat && (
                            <CardDescription className="text-green-300 print:text-green-800">
                                {PROMO_30_KITCHEN} · ROT på arbetskostnad
                            </CardDescription>
                        )}
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div>
                            <div className="text-sm opacity-70">Rekommenderat Fastpris ({isPrivat ? 'Inkl' : 'Exkl'} moms)</div>
                            <div className="text-3xl font-bold">{fmtKr(result.price_rec)}</div>
                        </div>
                        {isPrivat && n(result.rot_deduction) != null && n(result.rot_deduction) > 0 && n(result.price_rec) != null && (
                            <div className="text-sm text-green-400 print:text-green-700">
                                Preliminärt ROT-avdrag: -{n(result.rot_deduction).toLocaleString('sv-SE')} kr
                                <div className="font-bold border-t border-green-400 mt-1 pt-1">
                                  Att betala:{' '}
                                  {(n(result.price_rec) - n(result.rot_deduction)).toLocaleString('sv-SE')} kr
                                </div>
                            </div>
                        )}
                        <div className="grid grid-cols-2 gap-4 text-sm mt-2 pt-2 border-t border-slate-700 print:border-slate-300">
                            <div>
                                <div className="opacity-70">Lågt estimat</div>
                                <div>{fmtKr(result.price_low)}</div>
                            </div>
                            <div>
                                <div className="opacity-70">Högt estimat</div>
                                <div>{fmtKr(result.price_high)}</div>
                            </div>
                        </div>
                        <div className="mt-4 pt-4 border-t border-slate-700 print:border-slate-300">
                             <div className="text-sm font-bold mb-1">Underlagsscore: {result.score ?? 0}/100</div>
                             {result.flags && result.flags.length > 0 ? (
                                <ul className="list-disc pl-4 text-xs text-red-300 print:text-red-700">
                                    {result.flags.map((flag, i) => <li key={i}>{flag}</li>)}
                                </ul>
                             ) : <div className="text-xs text-green-400 print:text-green-700">Inga anmärkningar</div>}
                        </div>
                    </CardContent>
                </Card>

                {/* Scope Description (Ingår/Ej Ingår) - NEW */}
                <Card className="print:shadow-none print:border print-break-inside">
                    <CardHeader>
                        <CardTitle>Omfattning</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4 text-sm">
                        <div>
                            <div className="font-bold mb-2 flex items-center gap-2"><Check className="h-4 w-4 text-green-600" /> Ingår</div>
                            <ul className="list-disc pl-4 space-y-1 text-muted-foreground">
                                {Array.isArray(result.scope_included) && result.scope_included.length > 0
                                  ? result.scope_included.map((item, i) => <li key={i}>{item}</li>)
                                  : <li>Specifikation saknas</li>}
                            </ul>
                        </div>
                        <Separator />
                        <div>
                             <div className="font-bold mb-2 flex items-center gap-2"><X className="h-4 w-4 text-red-500" /> Ingår ej</div>
                            <ul className="list-disc pl-4 space-y-1 text-muted-foreground">
                                {Array.isArray(result.scope_excluded) && result.scope_excluded.length > 0
                                  ? result.scope_excluded.map((item, i) => <li key={i}>{item}</li>)
                                  : <li>Specifikation saknas</li>}
                            </ul>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Right: Breakdown & Config */}
            <div className="md:col-span-2 space-y-6">
                 {/* Calculation Breakdown Table */}
                 <Card className="print:shadow-none print:border">
                    <CardHeader>
                        <CardTitle>Kalkylspecifikation (Timmar)</CardTitle>
                    </CardHeader>
                    <CardContent>
                        {Array.isArray(result.breakdown) && result.breakdown.length > 0 ? (
                            <Table className="text-sm">
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Kategori</TableHead>
                                        <TableHead>Beskrivning</TableHead>
                                        <TableHead className="text-right">Antal</TableHead>
                                        <TableHead className="text-right">Med (h)</TableHead>
                                        <TableHead className="text-right">Totalt (h)</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {result.breakdown.map((row, idx) => (
                                        <TableRow key={idx}>
                                            <TableCell className="font-medium text-xs text-muted-foreground">{row.category ?? NOT_FILLED}</TableCell>
                                            <TableCell>{row.description ?? NOT_FILLED}</TableCell>
                                            <TableCell className="text-right">{row.count ?? NOT_FILLED}</TableCell>
                                            <TableCell className="text-right text-muted-foreground">{row.med ?? NOT_FILLED}</TableCell>
                                            <TableCell className="text-right font-bold">{row.total_med ?? NOT_FILLED}</TableCell>
                                        </TableRow>
                                    ))}
                                    <TableRow className="bg-slate-50 font-bold">
                                        <TableCell colSpan={4} className="text-right">Summa Timmar (Median)</TableCell>
                                        <TableCell className="text-right">{result.hours_median ?? NOT_FILLED} {result.hours_median == null ? '' : 'h'}</TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        ) : (
                            <div className="text-muted-foreground text-sm">Ingen specifikation tillgänglig för detta ärende (gammal version).</div>
                        )}
                    </CardContent>
                </Card>

                <Card className="print:shadow-none print:border print-break-inside">
                    <CardHeader>
                        <CardTitle>Vald Konfiguration</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-2 gap-y-4 gap-x-8 text-sm">
                            <div><span className="font-semibold">Skåp:</span> U: {config.u_cabinets ?? 0}, V: {config.v_cabinets ?? 0}, H: {config.h_cabinets ?? 0}</div>
                            <div><span className="font-semibold">Bänkskiva:</span> {config.worktop ?? NOT_FILLED}</div>
                            <div><span className="font-semibold">Vitvaror:</span> {((config.integrated_dishwasher ?? 0) + (config.oven_micro ?? 0) + (config.integrated_fridge_freezer ?? 0) + (config.hob ?? 0) + (config.fan ?? 0))} st totalt</div>
                            <div><span className="font-semibold">Täcksidor:</span> {config.panels ?? NOT_FILLED}</div>
                            <div><span className="font-semibold">El / VVS:</span> {(config.electrical ?? NOT_FILLED)} / {(config.plumbing ?? NOT_FILLED)}</div>
                            <div><span className="font-semibold">Golv:</span> {config.floor_laid === true ? "Klart" : config.floor_laid === false ? "Ej klart" : NOT_FILLED}</div>
                            <div><span className="font-semibold">Målning:</span> 
                                {(n(config.painting_walls) ?? 0) > 0 || (n(config.painting_ceiling) ?? 0) > 0 ? (
                                    <>
                                        {(n(config.painting_walls) ?? 0) > 0 && ` Vägg: ${config.painting_walls}m²`}
                                        {(n(config.painting_ceiling) ?? 0) > 0 && ` Tak: ${config.painting_ceiling}m²`}
                                    </>
                                ) : " Ingen"}
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card className="print-break-inside print:shadow-none print:border">
                    <CardHeader>
                        <CardTitle>Foton & Mått</CardTitle>
                    </CardHeader>
                    <CardContent>
                         <div className="text-sm mb-4">
                            <span className="font-bold">Mått:</span>{' '}
                            A: {dimensions?.wall_a ?? NOT_FILLED}{dimensions?.wall_a == null ? '' : 'mm'}, B: {dimensions?.wall_b ?? NOT_FILLED}{dimensions?.wall_b == null ? '' : 'mm'}, Höjd: {dimensions?.ceiling_height ?? NOT_FILLED}{dimensions?.ceiling_height == null ? '' : 'mm'}
                         </div>
                         <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 no-print">
                            {photoList.map((photo, i) => (
                                <button
                                  key={i}
                                  type="button"
                                  onClick={() => setLightboxIndex(i)}
                                  className="group relative aspect-square bg-slate-100 rounded-lg overflow-hidden ring-offset-2 hover:opacity-95 hover:ring-2 hover:ring-primary/40 focus:outline-none focus:ring-2 focus:ring-primary text-left cursor-zoom-in"
                                  title="Klicka för stor bild"
                                >
                                    <img
                                      src={resolvePhotoUrl(photo.url)}
                                      alt={`Kundfoto ${i + 1}`}
                                      className="object-cover w-full h-full transition-transform group-hover:scale-[1.02]"
                                    />
                                    <span className="absolute bottom-1 right-1 rounded bg-black/55 text-white text-[10px] px-1.5 py-0.5 opacity-0 group-hover:opacity-100">
                                      Visa
                                    </span>
                                </button>
                            ))}
                        </div>
                        <div className="hidden print:block text-xs text-muted-foreground">
                            (Foton bifogas ej i utskrift för att spara bläck/plats - se digital version)
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
      </div>

      <Dialog open={lightboxIndex !== null} onOpenChange={(open) => !open && setLightboxIndex(null)}>
        <DialogContent className="max-w-[min(96vw,1280px)] w-full p-3 sm:p-6 gap-2 border bg-background">
          <DialogHeader className="sr-only">
            <DialogTitle>
              Foto {lightboxIndex !== null ? lightboxIndex + 1 : 0} av {photoList.length}
            </DialogTitle>
            <DialogDescription>Förstorad bild. Piltangenter byter bild.</DialogDescription>
          </DialogHeader>
          {lightboxIndex !== null && photoList[lightboxIndex] && (
            <div className="relative flex flex-col items-center gap-3">
              <div className="relative w-full flex items-center justify-center min-h-[min(70vh,600px)]">
                {photoList.length > 1 && (
                  <>
                    <Button
                      type="button"
                      variant="secondary"
                      size="icon"
                      className="absolute left-0 sm:left-1 z-10 h-10 w-10 rounded-full shadow-md"
                      onClick={() =>
                        setLightboxIndex((i) =>
                          i === null ? i : i === 0 ? photoList.length - 1 : i - 1
                        )
                      }
                      aria-label="Föregående bild"
                    >
                      <ChevronLeft className="h-5 w-5" />
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      size="icon"
                      className="absolute right-0 sm:right-1 z-10 h-10 w-10 rounded-full shadow-md"
                      onClick={() =>
                        setLightboxIndex((i) =>
                          i === null ? i : i === photoList.length - 1 ? 0 : i + 1
                        )
                      }
                      aria-label="Nästa bild"
                    >
                      <ChevronRight className="h-5 w-5" />
                    </Button>
                  </>
                )}
                <img
                  src={resolvePhotoUrl(photoList[lightboxIndex].url)}
                  alt=""
                  className="max-w-full max-h-[min(82vh,920px)] w-auto h-auto object-contain rounded-md"
                />
              </div>
              <div className="flex flex-wrap items-center justify-center gap-3 text-sm text-muted-foreground">
                <span>
                  Bild {lightboxIndex + 1} / {photoList.length}
                </span>
                <a
                  href={resolvePhotoUrl(photoList[lightboxIndex].url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-primary hover:underline font-medium"
                >
                  <ExternalLink className="h-4 w-4" />
                  Öppna i ny flik
                </a>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
